/**
 * GroceryHouseholdDrawer — one household, as a GROCER needs it: who they are,
 * where to deliver, what they ordered, and nothing else.
 *
 * WHAT IS NOT HERE, ON PURPOSE AND SAID OUT LOUD ON SCREEN: the unsent list,
 * favourites, item preferences and every chef conversation. The server never
 * sends them, so this file cannot draw them even by mistake.
 *
 * The step-by-step order history is the SHARED components/shared/ActionTimeline
 * the Command Center already uses for every other vertical — not a copy of it.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2, Phone, MapPin, Users, ShoppingBasket, EyeOff, Package } from 'lucide-react';
import api from '../../../lib/api';
import ActionTimeline from '../../../components/shared/ActionTimeline';

const StatusPill = ({ status, delayed }) => {
    const { t } = useTranslation();
    const tone = delayed ? 'bg-amber-100 text-amber-800'
        : status === 'delivered' ? 'bg-emerald-100 text-emerald-800'
        : status === 'cancelled' ? 'bg-slate-100 text-slate-600'
        : 'bg-sky-100 text-sky-800';
    return (
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${tone}`}>
            {t(`grocery.cc.status_${status}`, status)}
        </span>
    );
};

const OrderBlock = ({ order, testid }) => {
    const { t } = useTranslation();
    return (
        <div className="rounded-xl border border-slate-200 p-3 dark:border-slate-700/50" data-testid={testid}>
            <div className="flex flex-wrap items-center gap-2">
                <StatusPill status={order.status} delayed={order.delayed} />
                <span className="text-xs text-muted-foreground">
                    {order.window?.day} · {order.window?.start}–{order.window?.end}
                </span>
                <span className="text-xs font-semibold text-foreground">
                    {t('grocery.cc.itemCount', { count: order.item_count })}
                </span>
            </div>
            <ul className="mt-2 grid grid-cols-1 gap-1 sm:grid-cols-2">
                {order.items.map((i, n) => (
                    <li key={`${order.id}-${n}`} className="flex items-center gap-1.5 text-xs text-foreground">
                        <span className="truncate">{i.name} ({i.qty})</span>
                        {i.custom ? (
                            <span className="shrink-0 rounded bg-violet-100 px-1.5 text-[10px] font-bold uppercase text-violet-700"
                                data-testid={`grocery-cc-custom-line-${order.id}-${n}`}>
                                {t('grocery.cc.customLine')}
                            </span>
                        ) : null}
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default function GroceryHouseholdDrawer({ organizationId, householdId, onClose }) {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await api.get(`/grocery/admin/households/${householdId}`,
                    { params: { organization_id: organizationId } });
                if (alive) setData(r.data);
            } catch (_e) { if (alive) setError(true); }
        })();
        return () => { alive = false; };
    }, [householdId, organizationId]);

    const timeline = useCallback(async () => {
        const r = await api.get(`/grocery/admin/households/${householdId}/timeline`,
            { params: { organization_id: organizationId } });
        return r.data;
    }, [householdId, organizationId]);

    const labels = useMemo(() => ({
        title: t('grocery.cc.orderSteps'), loading: t('common.loading', 'Loading…'),
        empty: t('grocery.cc.noSteps'),
    }), [t]);

    return (
        <div className="fixed inset-0 z-[70] flex justify-end bg-slate-900/50"
            data-testid="grocery-cc-household-drawer" onClick={onClose}>
            <div className="h-full w-full max-w-xl overflow-y-auto bg-white p-5 shadow-2xl dark:bg-slate-950"
                onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <p className="text-lg font-bold text-foreground" data-testid="grocery-cc-hh-name">
                            {data?.name || '—'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                            #{data?.card_id} · {data?.store?.name} · {(data?.language || '').toUpperCase()}
                        </p>
                    </div>
                    <button onClick={onClose} aria-label={t('common.close', 'Close')}
                        data-testid="grocery-cc-hh-close"
                        className="flex h-11 w-11 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {!data && !error ? (
                    <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>
                ) : error ? (
                    <p className="py-10 text-sm text-muted-foreground" data-testid="grocery-cc-hh-error">
                        {t('grocery.cc.householdFailed')}
                    </p>
                ) : (
                    <div className="mt-4 space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            {[['orders', data.orders.total], ['streak', data.orders.streak_weeks],
                              ['members', data.member_count], ['lastOrder', data.orders.last_order_day || '—']]
                                .map(([key, value]) => (
                                <div key={key} className="rounded-xl border border-slate-200 p-3 dark:border-slate-700/50"
                                    data-testid={`grocery-cc-hh-${key}`}>
                                    <p className="text-xl font-bold text-foreground">{value}</p>
                                    <p className="text-xs text-muted-foreground">{t(`grocery.cc.hh_${key}`)}</p>
                                </div>
                            ))}
                        </div>

                        <section className="rounded-xl border border-slate-200 p-3 dark:border-slate-700/50"
                            data-testid="grocery-cc-hh-delivery">
                            <p className="flex items-center gap-2 text-sm font-bold text-foreground">
                                <MapPin className="h-4 w-4 text-emerald-500" />{t('grocery.cc.deliveryTo')}
                            </p>
                            <p className="mt-1 text-sm text-foreground">
                                {data.address.street}{data.address.apartment ? `, ${data.address.apartment}` : ''}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                {data.address.city} {data.address.postal_code}
                            </p>
                            <p className="mt-2 flex flex-wrap items-center gap-2 text-sm text-foreground">
                                <Phone className="h-4 w-4 text-slate-400" />
                                <span data-testid="grocery-cc-hh-phone">
                                    {data.contact.phone || data.contact.phone_masked || '—'}
                                </span>
                                {data.contact.phone ? (
                                    <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-bold uppercase text-sky-800"
                                        data-testid="grocery-cc-hh-phone-why">
                                        {t('grocery.cc.phoneBecauseOpenOrder')}
                                    </span>
                                ) : null}
                            </p>
                            <p className="text-xs text-muted-foreground" data-testid="grocery-cc-hh-email">
                                {data.contact.email_masked || '—'}
                            </p>
                            <p className="mt-2 text-xs text-muted-foreground" data-testid="grocery-cc-hh-consents">
                                {t('grocery.cc.serviceMessages')}: {data.consents.service_messages ? t('common.yes', 'Yes') : t('common.no', 'No')}
                                {' · '}{t('grocery.cc.promotions')}: {data.consents.promotions ? t('common.yes', 'Yes') : t('common.no', 'No')}
                            </p>
                        </section>

                        <section className="rounded-xl border border-slate-200 p-3 dark:border-slate-700/50"
                            data-testid="grocery-cc-hh-members">
                            <p className="flex items-center gap-2 text-sm font-bold text-foreground">
                                <Users className="h-4 w-4 text-emerald-500" />{t('grocery.cc.whoLivesHere')}
                            </p>
                            <p className="mt-1 text-sm text-foreground">
                                {data.members.map((m) => m.name).join(' · ')}
                            </p>
                        </section>

                        {data.orders.open.length ? (
                            <section className="space-y-2" data-testid="grocery-cc-hh-open-orders">
                                <p className="flex items-center gap-2 text-sm font-bold text-foreground">
                                    <Package className="h-4 w-4 text-sky-500" />{t('grocery.cc.currentOrder')}
                                </p>
                                {data.orders.open.map((o) => (
                                    <OrderBlock key={o.id} order={o} testid={`grocery-cc-hh-order-${o.id}`} />
                                ))}
                                <ActionTimeline fetcher={timeline} labels={labels}
                                    testid="grocery-cc-hh-timeline" />
                            </section>
                        ) : null}

                        {data.top_items.length ? (
                            <section className="rounded-xl border border-slate-200 p-3 dark:border-slate-700/50"
                                data-testid="grocery-cc-hh-top-items">
                                <p className="flex items-center gap-2 text-sm font-bold text-foreground">
                                    <ShoppingBasket className="h-4 w-4 text-emerald-500" />
                                    {t('grocery.cc.topItemsFromOrders')}
                                </p>
                                <ul className="mt-2 grid grid-cols-2 gap-1">
                                    {data.top_items.map((i) => (
                                        <li key={i.catalog_id || i.name} className="truncate text-xs text-foreground">
                                            {i.name} <span className="text-muted-foreground">· {i.orders}</span>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        ) : null}

                        {data.orders.past.length ? (
                            <section className="space-y-2" data-testid="grocery-cc-hh-past-orders">
                                <p className="text-sm font-bold text-foreground">{t('grocery.cc.pastOrders')}</p>
                                {data.orders.past.map((o) => (
                                    <OrderBlock key={o.id} order={o} testid={`grocery-cc-hh-past-${o.id}`} />
                                ))}
                            </section>
                        ) : null}

                        {/* Said in words, so nobody wonders whether it was forgotten. */}
                        <p className="flex items-start gap-2 rounded-xl bg-slate-50 p-3 text-xs text-muted-foreground dark:bg-slate-900/60"
                            data-testid="grocery-cc-hh-withheld">
                            <EyeOff className="mt-0.5 h-4 w-4 shrink-0" />
                            {t('grocery.cc.withheldNote')}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
